import { fetchExperiencesById, fetchExperienceSearchList } from 'actions/ExperienceActions'
import { take } from 'lib/array/arrayUtils'
import { useEffect, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useExperienceSearchList from './useExperienceSearchList'

interface SearchProps {
  placeId?: string;
  placeIdToIgnore?: string;
  limit?: number;
  sortBy?: Experiences.SortTypes;
  excludeIds?: Array<string>;
  campaigns?: Array<string>;
  randomize?: boolean;
  enabled?: boolean;
}

function useExperienceSearch(
  searchProps: SearchProps,
  filterProps: Experiences.SearchFilters = {},
): [App.ApiCallState<{ ids: Array<string>, placeId: string | undefined }>, Array<App.ExperienceOffer | undefined>] {
  const {
    placeId,
    placeIdToIgnore,
    limit = 8,
    sortBy,
    excludeIds = [],
    campaigns,
    randomize,
    enabled = true,
  } = searchProps
  const {
    categoryCodes,
    priceLte,
    priceGte,
    luxPlusFeatures,
  } = filterProps
  const dispatch = useAppDispatch()

  const experienceList = useExperienceSearchList({ categoryCodes, placeId, placeIdToIgnore, sortBy, excludeIds, priceLte, priceGte, campaigns, randomize, enabled, luxPlusFeatures })
  const experiences = useAppSelector((state) => state.experience.experiences)

  useEffect(() => {
    if (placeId) {
      dispatch(fetchExperienceSearchList(placeId, { categoryCodes, sortBy, priceLte, priceGte, campaigns }))
    }
    // eslint-disable-next-line
  }, [placeId, categoryCodes, sortBy, campaigns])

  const experienceItems = useMemo(() => {
    if (experienceList) {
      const ids = take(experienceList.ids, limit)
      return ids.map(id => experiences[id])
    }
    return []
  }, [experienceList, limit, experiences])

  useEffect(() => {
    if (experienceList?.ids) {
      const ids = take(experienceList.ids, limit)
      dispatch(fetchExperiencesById(...ids))
    }
  }, [experienceList?.ids, limit, dispatch])

  return [
    experienceList,
    experienceItems,
  ]
}

export default useExperienceSearch
